import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/feature/VideoCard";
import EmptyState from "@/components/feature/EmptyState";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import type { VideoType } from "@/types/video";
import {
  getHotVideos,
  searchVideos,
  typeLabels,
} from "@/utils/video";

const hotKeywords = ["星际回声", "悬疑", "美食", "短视频", "家庭", "科幻"];

const typeTabs: { key: "all" | VideoType; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "series", label: "电视剧" },
  { key: "short", label: "短视频" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") ?? "";
  const [input, setInput] = useState(keyword);
  const [activeType, setActiveType] = useState<"all" | VideoType>("all");

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    setActiveType("all");
  }, [keyword]);

  const results = keyword ? searchVideos(keyword) : [];
  const filtered = activeType === "all" ? results : results.filter((item) => item.type === activeType);
  const hotVideos = getHotVideos(6);

  useDocumentMeta({
    title: keyword ? `“${keyword}”搜索结果 - 逼特逼视频` : "搜索视频 - 逼特逼视频",
    description: keyword
      ? `逼特逼视频为你找到与“${keyword}”相关的 ${results.length} 部视频内容，包含电影、电视剧与短视频。`
      : "在逼特逼视频搜索电影、电视剧与短视频，输入片名、演员、导演或题材即可快速定位。",
    keywords: keyword
      ? `${keyword},搜索视频,在线观看,逼特逼视频`
      : "搜索视频,影视搜索,短视频搜索,逼特逼视频",
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    setSearchParams(value ? { q: value } : {});
  };

  return (
    <div>
      <section className="border-b border-background-200 bg-background-100">
        <div className="page-shell py-8 md:py-12">
          <p className="mb-2 flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.2em] text-primary-400">
            <span className="h-px w-6 bg-primary-500" />
            Search
          </p>
          <h1 className="font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-3xl">
            {keyword ? (
              <>
                “<span className="text-primary-400">{keyword}</span>” 的搜索结果
              </>
            ) : (
              "搜索你感兴趣的视频"
            )}
          </h1>
          <p className="mt-2 text-sm text-foreground-600">
            {keyword
              ? `共找到 ${results.length} 部相关视频，可按类型快速筛选。`
              : "支持片名、简介、演员、导演与题材的关键词搜索，一次覆盖全站内容。"}
          </p>

          <form
            onSubmit={handleSubmit}
            className="mt-5 flex w-full max-w-2xl items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3 py-2 transition-colors focus-within:border-primary-400"
          >
            <span className="flex h-5 w-5 items-center justify-center text-foreground-500">
              <i className="ri-search-line text-lg" />
            </span>
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              placeholder="输入片名、演员或题材，例如：星际回声"
              aria-label="搜索关键词"
              className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 focus:outline-none"
            />
            {input && (
              <button
                type="button"
                aria-label="清空关键词"
                onClick={() => setInput("")}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-foreground-500 hover:text-foreground-900"
              >
                <i className="ri-close-line text-base" />
              </button>
            )}
            <button
              type="submit"
              className="shrink-0 whitespace-nowrap rounded bg-primary-500 px-4 py-1.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              搜索
            </button>
          </form>

          <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-[11px] text-foreground-500">热门搜索</span>
            {hotKeywords.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => setSearchParams({ q: item })}
                className="whitespace-nowrap rounded-full border border-background-200 bg-background-50 px-2.5 py-1 text-[11px] text-foreground-600 transition-colors hover:border-primary-400 hover:text-primary-400"
              >
                {item}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="page-shell py-8 md:py-10">
        {keyword && results.length > 0 && (
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
              {typeTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveType(tab.key)}
                  className={`whitespace-nowrap rounded-full px-3 py-1.5 font-label text-xs transition-colors ${
                    activeType === tab.key
                      ? "bg-primary-500 text-background-50"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {tab.label}
                  {tab.key !== "all" && (
                    <span className="ml-1 text-[10px] opacity-70">
                      {results.filter((item) => item.type === tab.key).length}
                    </span>
                  )}
                </button>
              ))}
            </div>
            <span className="whitespace-nowrap font-label text-xs text-foreground-500">
              当前显示 {filtered.length} 部
            </span>
          </div>
        )}

        {keyword && filtered.length === 0 ? (
          <>
            <EmptyState
              icon="ri-search-eye-line"
              title="未找到相关视频"
              description={`没有找到与“${keyword}”匹配的内容，换个关键词试试，或者看看下面的热门推荐。`}
            />
            <div className="mt-10">
              <h2 className="mb-4 font-heading text-lg font-semibold text-foreground-950">
                热门内容推荐
              </h2>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {hotVideos.slice(0, 4).map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>
          </>
        ) : keyword ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.map((video) => (
              <VideoCard key={video.id} video={video} showSummary />
            ))}
          </div>
        ) : (
          <>
            <div className="mb-7 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {(["movie", "series", "short"] as VideoType[]).map((type) => (
                <Link
                  key={type}
                  to={`/category/${type}`}
                  className="group flex items-center justify-between rounded-lg border border-background-200 bg-background-100 p-4 transition-colors hover:border-primary-400/70"
                >
                  <div>
                    <p className="font-heading text-sm font-medium text-foreground-900">
                      浏览全部{typeLabels[type]}
                    </p>
                    <p className="mt-1 text-xs text-foreground-500">
                      按题材与年份筛选{typeLabels[type]}
                    </p>
                  </div>
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-background-200 text-foreground-500 transition-colors group-hover:border-primary-400 group-hover:text-primary-400">
                    <i className="ri-arrow-right-line text-sm" />
                  </span>
                </Link>
              ))}
            </div>

            <h2 className="mb-4 font-heading text-lg font-semibold text-foreground-950">
              大家都在看
            </h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {hotVideos.map((video) => (
                <VideoCard key={video.id} video={video} showSummary />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}